/**
 * Accounting Index Page
 *
 * Landing page for the accounting module.
 * Provides navigation to all accounting features.
 */

import { Head, Link, usePage } from '@inertiajs/react';
import { Navbar } from '@/components';
import type { SharedProps } from '@/types';

interface AccountingIndexProps extends SharedProps {
  stats?: {
    pendingTransactions: number;
    suggestedRules: number;
    needsReview: number;
  };
  importStatus?: {
    failedCount: number;
    pendingCount: number;
    lastSyncAt: string | null;
  } | null;
}

export default function AccountingIndex() {
  const { stats, importStatus } = usePage<AccountingIndexProps>().props;

  const features = [
    {
      title: 'Transactions',
      description: 'View, filter, and classify your financial transactions',
      href: '/accounting/transactions',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      color: 'blue',
      badge: stats?.pendingTransactions ? `${stats.pendingTransactions} pending` : undefined,
    },
    {
      title: 'Import Configuration',
      description: 'Configure automatic bank transaction imports from Google Sheets',
      href: '/accounting/import',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      color: 'green',
    },
    {
      title: 'Reports',
      description: 'View spending, income, expenses, and account balance reports',
      href: '/accounting/reports',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
      color: 'cyan',
    },
    {
      title: 'Classification Metrics',
      description: 'View automation performance and classification statistics',
      href: '/accounting/classification',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: 'indigo',
    },
    {
      title: 'Suggested Rules',
      description: 'Review and approve AI-generated classification rules',
      href: '/accounting/suggested_rules',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      color: 'purple',
      badge: stats?.suggestedRules ? `${stats.suggestedRules} pending` : undefined,
    },
    {
      title: 'Classification Rules',
      description: 'Manage saved rules for automatic transaction classification',
      href: '/accounting/classification_rules',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      color: 'amber',
    },
  ];

  const colorClasses: Record<string, { bg: string; bgHover: string; text: string; border: string }> = {
    blue: { bg: 'bg-blue-100', bgHover: 'group-hover:bg-blue-200', text: 'text-blue-600', border: 'hover:border-blue-300' },
    green: { bg: 'bg-green-100', bgHover: 'group-hover:bg-green-200', text: 'text-green-600', border: 'hover:border-green-300' },
    cyan: { bg: 'bg-cyan-100', bgHover: 'group-hover:bg-cyan-200', text: 'text-cyan-600', border: 'hover:border-cyan-300' },
    indigo: { bg: 'bg-indigo-100', bgHover: 'group-hover:bg-indigo-200', text: 'text-indigo-600', border: 'hover:border-indigo-300' },
    purple: { bg: 'bg-purple-100', bgHover: 'group-hover:bg-purple-200', text: 'text-purple-600', border: 'hover:border-purple-300' },
    amber: { bg: 'bg-amber-100', bgHover: 'group-hover:bg-amber-200', text: 'text-amber-600', border: 'hover:border-amber-300' },
  };

  return (
    <>
      <Head title="Accounting" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="accounting" />

        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Accounting</h1>
            <p className="text-gray-600">
              Manage your financial transactions, configure imports, and track classification performance.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {features.map((feature) => {
              const colors = colorClasses[feature.color];
              return (
                <Link
                  key={feature.href}
                  href={feature.href}
                  className={`block p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md ${colors.border} transition-all group`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${colors.bg} ${colors.bgHover} rounded-lg transition-colors`}>
                      <div className={colors.text}>{feature.icon}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                          {feature.title}
                        </h2>
                        {feature.badge && (
                          <span className={`px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} rounded-full`}>
                            {feature.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {feature.description}
                      </p>
                    </div>
                    <svg
                      className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Import Issues Alert */}
          {importStatus && (importStatus.failedCount > 0 || importStatus.pendingCount > 0) && (
            <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Import Issues
              </h3>
              <p className="text-sm text-red-700">
                {importStatus.failedCount > 0 && (
                  <span>{importStatus.failedCount} row{importStatus.failedCount !== 1 ? 's' : ''} failed to import. </span>
                )}
                {importStatus.pendingCount > 0 && (
                  <span>{importStatus.pendingCount} row{importStatus.pendingCount !== 1 ? 's' : ''} pending processing. </span>
                )}
                <Link href="/accounting/import" className="underline hover:text-red-800">
                  Review and reprocess &rarr;
                </Link>
              </p>
            </div>
          )}

          {/* Quick Stats */}
          {stats && (stats.needsReview > 0 || stats.pendingTransactions > 0) && (
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Action Needed</h3>
              <ul className="space-y-1 text-sm text-amber-700">
                {stats.needsReview > 0 && (
                  <li>
                    <Link href="/accounting/transactions?status=needs_review" className="hover:underline">
                      {stats.needsReview} transaction{stats.needsReview !== 1 ? 's' : ''} need review
                    </Link>
                  </li>
                )}
                {stats.suggestedRules > 0 && (
                  <li>
                    <Link href="/accounting/suggested_rules" className="hover:underline">
                      {stats.suggestedRules} suggested rule{stats.suggestedRules !== 1 ? 's' : ''} to review
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
